/**
 * Selection Actions client — calls `/api/content/selection-action` for a
 * floating toolbar's Explain/Improve/Shorten/Fix grammar/Change tone acting
 * on a highlighted phrase inside a plain textarea/input.
 *
 * Mirrors `caption-rewrite.ts`'s error shape exactly (typed error class,
 * status → category mapping, retryAfter parsing) rather than inventing a
 * second convention for the same class of endpoint.
 */

import type { ErrorCategory } from '@/lib/errors/friendly-errors';

/**
 * `instruction` is the free-text verb: the caller supplies its own sentence
 * ("make it sound less corporate") instead of picking one of the four canned
 * rewrites. It exists because a floating toolbar with only preset verbs makes
 * the user translate what they want into the nearest button; see
 * `TextSelectionToolbar`'s `allowFreeform` prop.
 */
export type SelectionAction =
  'improve' | 'shorten' | 'grammar' | 'tone' | 'explain' | 'instruction';

/**
 * Matches the endpoint's own cap. It was 240, which is shorter than the job: an
 * instruction opened from a review note arrives pre-filled with the note's
 * suggestion, and the operator then types the replacement sentence after it.
 * The input's `maxLength` cut that silently mid-word and the model faithfully
 * applied the stump ("…review of each co Instead,", handoff 2026-10-15 §3.1).
 */
export const SELECTION_INSTRUCTION_MAX_LENGTH = 1000;

export class SelectionActionError extends Error {
  readonly name = 'SelectionActionError';
  readonly errorCode: ErrorCategory;
  readonly retryAfter?: number;
  readonly status: number;

  constructor(args: {
    message: string;
    errorCode: ErrorCategory;
    status: number;
    retryAfter?: number;
  }) {
    super(args.message);
    this.errorCode = args.errorCode;
    this.status = args.status;
    this.retryAfter = args.retryAfter;
  }
}

function categoryForStatus(status: number): ErrorCategory {
  if (status === 429) return 'rate_limit';
  if (status === 401 || status === 403) return 'auth';
  if (status === 408 || status === 504) return 'timeout';
  if (status >= 500) return 'server_error';
  return 'unknown';
}

function parseRetryAfter(body: unknown, header: string | null): number | undefined {
  const fromBody = (body as { retryAfter?: unknown } | null)?.retryAfter;
  if (typeof fromBody === 'number' && Number.isFinite(fromBody) && fromBody > 0) {
    return Math.ceil(fromBody);
  }
  const fromHeader = header ? Number(header) : NaN;
  if (Number.isFinite(fromHeader) && fromHeader > 0) return Math.ceil(fromHeader);
  return undefined;
}

export async function runSelectionAction(args: {
  text: string;
  action: SelectionAction;
  tone?: string;
  /** Required when `action === 'instruction'` — the user's own edit request. */
  instruction?: string;
  context?: string;
  /** Applies the account's saved brand voice when the surface explicitly opts in. */
  useBrandVoice?: boolean;
  accessToken: string;
}): Promise<string> {
  const { text, action, tone, instruction, context, useBrandVoice, accessToken } = args;
  if (!accessToken) {
    throw new Error('Selection action requires an access token');
  }
  if (!text.trim()) {
    throw new Error('Selection action requires selected text');
  }
  if (action === 'instruction' && !instruction?.trim()) {
    throw new Error('Selection action requires an instruction');
  }
  const response = await fetch('/api/content/selection-action', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ text, action, tone, instruction, context, useBrandVoice }),
  });
  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new SelectionActionError({
      message: data?.error || `Selection action failed: ${response.status}`,
      errorCode: categoryForStatus(response.status),
      status: response.status,
      retryAfter: parseRetryAfter(data, response.headers.get('Retry-After')),
    });
  }
  const data = (await response.json()) as { result?: string };
  if (typeof data.result !== 'string' || data.result.length === 0) {
    throw new SelectionActionError({
      message: 'Selection action returned an empty response',
      errorCode: 'server_error',
      status: response.status,
    });
  }
  return data.result;
}
